import "./DSAManage.scss";
import CooperPlank from "../../img/cooper-plank.png";
import OpeningQuotes from "../../img/opening-quotes.svg";
import ClosingQuotes from "../../img/closing-quotes.svg";
import BorderTop from "../../img/border-top.svg";
import BorderBottom from "../../img/border-bottom.svg";
import React, { ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { Button, Checkbox, Drawer, Input, Radio, Tooltip } from "antd";
import { ArrowDownOutlined, ArrowUpOutlined, DownOutlined, FilterOutlined, SearchOutlined } from "@ant-design/icons";
import { ScreenSetUpModal } from "../dsa/ScreenSetUpModal";
import { DesignView, EditScreenContentModal } from "../dsa/EditScreenContentModal";
import { AppContext } from "../../AppContext";
import { Spinner } from "../account/misc/Spinner";
import {
  DsaBoardDto,
  DsaBoardDtoTypeEnum,
  DsaPlaylistDto,
  DsaPlaylistEntryDto,
  DsaScheduleDto,
  DsaScreenDto,
  LocationDto
} from "../../generated/dashboard-client";
import DSAApi from "../../api/DSAApi";
import { handleServerError } from "../../util/ErrorHandler";
import { useRecoilState, useRecoilValue } from "recoil";
import { locationsState, memberState, mobileState } from "../../state/atoms";
import { AxiosResponse } from "axios";
import { ScreenSetUpHelpModal } from "../dsa/ScreenSetUpHelpModal";
import { ManageScreenModal } from "../dsa/ManageScreenModal";
import PreviewModal from "../dsa/PreviewModal";
import { RebootScreenModal } from "../dsa/RebootScreenModal";
import { RemoveScreenModal } from "../dsa/RemoveScreenModal";
import ScreenCard from "../dsa/ScreenCard";
import { DisplayDiagnosticsOnScreenConfirmationModal } from "../dsa/DisplayDiagnosticsOnScreenConfirmationModal";
import { DeactivateScreenModal } from "../dsa/DeactivateScreenModal";
import AvailableScreenCard from "../dsa/AvailableScreenCard";
import { DisplayUploadDto } from "../../domain/DisplayUploadDto";
import QRDisplayUploadsApi from "../../api/QRDisplayUploadsApi";
import StartPlayingIcon from "../icons/StartPlayingIcon";
import SortScreenIcon from "../icons/SortScreenIcon";
import CloseCircle from "../icons/CloseCircle";
import WiFiSignalOff from "../icons/WiFiSignalOff";
import { isDesignSubscription, isProSubscription } from "../../service/BillingService";
import DisplayIconSecondary from "../icons/DisplayIconSecondary";
import { onPairNewDevice, PAIR_NEW_DEVICE } from "../../service/DsaService";

enum SortType { none, screenNameAscending, screenNameDescending, locNameAscending, locNameDescending, contentTypeAscending, contentTypeDescending }
enum AvailabilityType { all, active, new, deactivated }

const SCREENS_BATCH_SIZE = 20;

export interface ScreenView extends DsaScreenDto {
  visible?: boolean;
  selected?: boolean;
}

interface DSAManageProps {
  activeLocation: LocationDto
  isMarketingBannerPage: boolean
}

const DsaContentDtoFilterTypeEnum = {
  Board: 'BOARD',
  Playlist: 'PLAYLIST',
  Schedule: 'SCHEDULE',
  None: 'NONE'
}

export default function DSAManage({ activeLocation, isMarketingBannerPage }: DSAManageProps) {
  const { location } = useContext(AppContext);
  const [locations] = useRecoilState(locationsState);
  const member = useRecoilValue(memberState);
  const mobile = useRecoilValue(mobileState);
  const [loading, setLoading] = useState(true);
  const [devicesLoading, setDevicesLoading] = useState(true);
  const [availableDevicesLoading, setAvailableDevicesLoading] = useState(true);
  const [deactivatedDevicesLoading, setDeactivatedDevicesLoading] = useState(true);
  const [reactivating, setReactivating] = useState(false);
  const [loadingContent, setLoadingContent] = useState(false);
  const [availableDevices, setAvailableDevices] = useState(0);
  const [availableDevicesPerLocation, setAvailableDevicesPerLocation] = useState<Record<number, number>>({});
  const [deactivatedDevices, setDeactivatedDevices] = useState(0);
  const [screens, setScreens] = useState([] as ScreenView[]);
  const [screensFetched, setScreensFetched] = useState(false);
  const [active, setActive] = useState(undefined as ScreenView | undefined);
  const [activeNumber, setActiveNumber] = useState(0);
  const [search, setSearch] = useState("");
  const [boards, setBoards] = useState([] as DsaBoardDto[]);
  const [playlists, setPlaylists] = useState([] as DsaPlaylistDto[]);
  const [schedules, setSchedules] = useState([] as DsaScheduleDto[]);
  const [uploads, setUploads] = useState([] as DisplayUploadDto[]);

  const [isSetUpModal, setSetUpModal] = useState(false);
  const [isSetUpHelpModal, setSetUpHelpModal] = useState(false);
  const [isEditContentModal, setEditContentModal] = useState(false);
  const [isManageScreenModal, setManageScreenModal] = useState(false);
  const [isRebootScreenModal, setRebootScreenModal] = useState(false);
  const [isDiagnosticsScreenModal, setDiagnosticsScreenModal] = useState(false);
  const [isRemoveScreenModal, setRemoveScreenModal] = useState(false);
  const [isDeactivateScreenModal, setDeactivateScreenModal] = useState(false);

  const [isPreviewModal, setPreviewModal] = useState(false);
  const [previewType, setPreviewType] = useState('');
  const [previewBoard, setPreviewBoard] = useState({} as DsaBoardDto);
  const [previewEntries, setPreviewEntries] = useState([] as DsaPlaylistEntryDto[]);
  const [previewUploadId, setPreviewUploadId] = useState('');

  const [sortType, setSortType] = useState(SortType.none);
  const [isFilterAllLocs, setFilterAllLocs] = useState(true);
  const [isFilterAllStatuses, setFilterAllStatuses] = useState(true);
  const [isFilterAllContentTypes, setFilterAllContentTypes] = useState(true);
  const [filteredLocs, setFilteredLocs] = useState([] as number[]);
  const [filteredStatuses, setFilteredStatuses] = useState([] as string[]);
  const [filteredContentTypes, setFilteredContentTypes] = useState([] as string[]);
  const [numFilters, setNumFilters] = useState(0);
  const [screenAvailabilityFilter, setScreenAvailabilityFilter] = useState(AvailabilityType.active);
  const [showAllFilterLocations, setShowAllFilterLocations] = useState(false);

  const [isMobileSortMenuVisible, setMobileSortMenuVisible] = useState(false);
  const [isMobileFilterMenuVisible, setMobileFilterMenuVisible] = useState(false);
  const [hasScreens, setHasScreens] = useState(false);
  const [visibleCount, setVisibleCount] = useState(SCREENS_BATCH_SIZE);
  // Serials whose live device details (online/wifi/hdmi) have already been fetched
  const [detailsLoaded, setDetailsLoaded] = useState(new Set<string>());

  const showData = (isProSubscription(member) && hasScreens) || member.enterpriseDisplayAccount;
  const bannerOffset = isMarketingBannerPage ? 60 : 0;

  const fetchDevices = useCallback(() => {
    if (!locations.length) return;
    if (screensFetched) return;
    setDevicesLoading(true);
    // Load all screen rows fast from the DB without live device details; details are
    // fetched lazily in batches for the screens actually shown (see effect below).
    DSAApi.getScreens(false).then(r => {
      let screens = r.data as ScreenView[];
      const locIds = locations.map(loc => loc.internalId);
      screens = screens.filter(sc => { return locIds.includes(sc.locationId) });
      screens.forEach(sc => sc.visible = true);
      setDevicesLoading(false);
      setScreensFetched(true);
      if (localStorage.getItem('dsa-just-ordered-device')) {
        setScreenAvailabilityFilter(AvailabilityType.new);
        localStorage.removeItem('dsa-just-ordered-device');
      } else if (screens.length === 0) {
        setScreenAvailabilityFilter(AvailabilityType.all);
      }
      setScreens([...screens]);
    }).catch(e => {
      handleServerError(e, "Failed to fetch devices.");
    });
  }, [locations, screensFetched]);

  const fetchAvailableDevices = useCallback(() => {
    // TODO remove this?
    if (member.email === "sindhu+display@musthavemenus.com" || member.email === 'sindhu+display.user@musthavemenus.com' || member.email === 'itemlibrarytoast1@itemlibrarytoast.com') {
      const ava = {} as Record<number, number>;
      setAvailableDevicesLoading(false);
      setAvailableDevices(
        locations
          .filter(
            (it) => isFilterAllLocs || filteredLocs.includes(it.internalId || 0)
          )
          .map((it) => (ava[it.internalId!] = it.availableScreens || 0))
          .reduce((sum, o) => sum + o, 0)
      );
      setAvailableDevicesPerLocation(ava);
    } else {
      DSAApi.getScreensTotal()
        .then((r) => {
          setAvailableDevicesLoading(false);
          setAvailableDevices(r.data);
          setAvailableDevicesPerLocation({ 0: r.data });
        })
        .catch((e) => {
          handleServerError(e, "Failed to fetch available devices.");
        });
    }
  }, [isFilterAllLocs, filteredLocs, locations, member.email]);

  const fetchDeactivatedDevices = useCallback(() => {
    setDeactivatedDevicesLoading(true);
    DSAApi.getDeactivatedScreensTotal().then(r => {
      setDeactivatedDevicesLoading(false);
      setDeactivatedDevices(r.data);
    }).catch(e => {
      handleServerError(e, "Failed to fetch deactivated devices.");
    });
  }, []);

  useEffect(fetchDevices, [fetchDevices]);
  useEffect(fetchAvailableDevices, [fetchAvailableDevices]);
  useEffect(fetchDeactivatedDevices, [fetchDeactivatedDevices]);

  useEffect(() => {
    setHasScreens(old => {
      if (screens.length === 0 && availableDevices === 0 && deactivatedDevices === 0) return false;
      if (old) return old;
      return screens.length + availableDevices + deactivatedDevices > 0;
    });
  }, [screens.length, availableDevices, deactivatedDevices])

  useEffect(() => {
    setLoading(devicesLoading || availableDevicesLoading || deactivatedDevicesLoading);
  }, [devicesLoading, availableDevicesLoading, deactivatedDevicesLoading])

  // Reset the lazy-load batch whenever the visible set of screens changes (search/filter/availability)
  useEffect(() => {
    setVisibleCount(SCREENS_BATCH_SIZE);
  }, [search, filteredLocs, filteredStatuses, filteredContentTypes, screenAvailabilityFilter])

  // Lazily fetch live device details (online/wifi/hdmi/ip) for the screens currently shown,
  // in batches, so the initial load stays fast even with hundreds of screens.
  useEffect(() => {
    if (!screensFetched) return;
    if (screenAvailabilityFilter !== AvailabilityType.active && screenAvailabilityFilter !== AvailabilityType.all) return;
    const missing = screens
      .filter(it => it.visible)
      .slice(0, visibleCount)
      .map(it => it.serial)
      .filter((s): s is string => !!s && !detailsLoaded.has(s));
    if (missing.length === 0) return;
    // Mark optimistically so a re-render doesn't re-request the same serials.
    setDetailsLoaded(prev => {
      const next = new Set(prev);
      missing.forEach(s => next.add(s));
      return next;
    });
    DSAApi.getScreensDetails(missing).then(r => {
      const bySerial = new Map((r.data as DsaScreenDto[]).map(d => [d.serial, d]));
      setScreens(prev => prev.map(sc => {
        const d = sc.serial ? bySerial.get(sc.serial) : undefined;
        return d ? { ...sc, ...d } : sc;
      }));
    }).catch(e => {
      // Allow a retry on the next render if the batch failed.
      setDetailsLoaded(prev => {
        const next = new Set(prev);
        missing.forEach(s => next.delete(s));
        return next;
      });
      handleServerError(e, "Failed to load screen details.");
    });
  }, [screens, visibleCount, screenAvailabilityFilter, screensFetched, detailsLoaded])

  useEffect(() => {
    if (!loading && !hasScreens) {
      if (isProSubscription(member) && !member.enterpriseDisplayAccount) {
        window.MHM.ProModal.addDSA({
          memberId: `${member.ownerId}` || member.id,
          locationId: activeLocation.internalId,
          onSuccess: () => {
            window.location.reload();
          },
        });
      }
    }
  }, [loading, member, hasScreens, activeLocation.internalId, screenAvailabilityFilter]);

  useEffect(() => {
    Promise.all(
      screens
        .filter(it => it.content?.type === 'BOARD')
        .map(it => DSAApi.getBoard(it.content?.sourceId || ''))
    ).then((value: AxiosResponse<DsaBoardDto>[]) => {
      setBoards(value.map(it => it.data));
    });
  }, [screens]);

  useEffect(() => {
    Promise.all(
      screens
        .filter(it => it.content?.type === 'PLAYLIST')
        .map(it => DSAApi.getPlaylist(it.content?.sourceId || ''))
    ).then((value: AxiosResponse<DsaBoardDto>[]) => {
      setPlaylists(value.map(it => it.data));
    });
  }, [screens]);

  useEffect(() => {
    DSAApi.getSchedules().then(resp => {
      setSchedules(resp.data);
    });
  }, []);

  useEffect(() => {
    QRDisplayUploadsApi.getDisplayUploadsByLocationIds(locations.map(l => l.internalId!)).then(resp => {
      setUploads(resp.data);
    });
  }, [locations]);

  function onUpgradeClick() {
    if (isDesignSubscription(member)) {
      window.MHM.ProModal.switchToProWithOverview({
        memberId: member.id,
        onSuccess: () => {
          sessionStorage.setItem('load-after-upgrade', 'true');
          window.location.reload();
        }
      });
    } else {
      window.MHM.ProModal.upgrade({
        memberId: member.id,
        onSuccess: () => {
          sessionStorage.setItem('load-after-upgrade', 'true');
          window.location.reload();
        }
      });
    }
  }

  useEffect(() => {
    if (sessionStorage.getItem('load-after-upgrade')) {
      sessionStorage.removeItem('load-after-upgrade');
      onOrderDevice().then();
    }
  })

  useEffect(() => {
    if (sessionStorage.getItem('setup-after-locadd')) {
      setSetUpModal(true);
    }
  }, [setSetUpModal])

  async function onOrderDevice() {
    if (!isProSubscription(member)) {
      if (isDesignSubscription(member)) {
        window.MHM.ProModal.switchToProWithOverview({ memberId: member.id, onSuccess: () => window.location.reload() });
      } else {
        window.MHM.ProModal.upgrade({ memberId: member.id, onSuccess: () => window.location.reload() });
      }
      return;
    }

    window.MHM.ProModal.addDSA({
      memberId: `${member.ownerId}` || member.id,
      locationId: activeLocation.internalId,
      onSuccess: () => {
        window.localStorage.setItem("dsa-just-ordered-device", "true");
        window.location.reload();
      }
    });
  }

  function onScreenAdded(screen: DsaScreenDto) {
    const callback = () => {
      setScreens([{ ...screen, visible: true }, ...screens]);
      setSetUpModal(false);
      setScreensFetched(false);
      fetchDevices();
      fetchAvailableDevices();
      setScreenAvailabilityFilter(AvailabilityType.active);
    }
    if (member.enterpriseDisplayAccount) {
      if (availableDevices <= 0) {
        reactivateScreenLicense(callback);
      } else {
        callback();
      }
    } else {
      callback();
    }
  }

  const setupModalWithPayment = useCallback(() => {
    setSetUpModal(true);
  }, []);

  useEffect(() => {
    document.addEventListener(PAIR_NEW_DEVICE, setupModalWithPayment);
    return () => document.removeEventListener(PAIR_NEW_DEVICE, setupModalWithPayment);
  }, [setupModalWithPayment]);

  function onScreenUpdated(screen: DsaScreenDto, timezone: string, rotation: number, scheduledReboot: boolean, restartDay: number, restartTime: string) {
    const thisScreen = screens.find(it => it.serial === screen.serial);
    if (thisScreen) {
      thisScreen.name = screen.name;
      thisScreen.locationId = screen.locationId;
      thisScreen.placement = screen.placement;
      thisScreen.timezone = timezone;
      thisScreen.rotation = rotation;
      thisScreen.scheduledReboot = scheduledReboot;
      if (scheduledReboot) {
        thisScreen.restartDay = restartDay;
        thisScreen.restartTime = restartTime;
      } else {
        thisScreen.restartDay = 0;
        thisScreen.restartTime = '';
      }
      setScreens([...screens]);
    }
    onCloseManageScreenModal();
  }

  function onCloseManageScreenModal() {
    setActive({});
    setManageScreenModal(false);
  }

  async function onPlaylistSelect(playlist: DsaPlaylistDto) {
    setEditContentModal(false);
    try {
      setLoadingContent(true);
      const content = await DSAApi.setScreenContent(active?.serial!!, {
        type: "PLAYLIST",
        sourceId: playlist.id,
        playable: true
      });

      setLoadingContent(false);
      setScreens([{ ...content.data, visible: true }, ...screens.filter(it => it.serial !== content.data.serial)]);
    } catch (e) {
      setLoadingContent(false);
      handleServerError(e);
    }
  }
  async function onScheduleSelect(schedule: DsaScheduleDto) {
    setEditContentModal(false);
    try {
      setLoadingContent(true);
      const content = await DSAApi.setScreenContent(active?.serial!!, {
        type: "SCHEDULE",
        sourceId: schedule.id,
        playable: true
      });

      setLoadingContent(false);
      setScreens([{ ...content.data, visible: true }, ...screens.filter(it => it.serial !== content.data.serial)]);
    } catch (e) {
      setLoadingContent(false);
      handleServerError(e);
    }
  }

  async function onDesignSelect(design: DesignView) {
    setEditContentModal(false);
    try {
      setLoadingContent(true);
      const board = await DSAApi.createBoard({
        type: DsaBoardDtoTypeEnum.Design,
        designId: design.id,
        page: design.page,
        name: design.name,
        memberId: member.internalId,
        locationId: location.internalId
      });

      const content = await DSAApi.setScreenContent(active?.serial!!, {
        type: "BOARD",
        sourceId: board.data.id,
        playable: true
      });

      setLoadingContent(false);
      setScreens([{ ...content.data, visible: true }, ...screens.filter(it => it.serial !== content.data.serial)]);
    } catch (e) {
      setLoadingContent(false);
      handleServerError(e);
    }
  }

  async function onUploadSelect(upload: DisplayUploadDto) {
    setEditContentModal(false);
    try {
      setLoadingContent(true);
      const board = await DSAApi.createBoard({
        type: DsaBoardDtoTypeEnum.Upload,
        name: upload.name,
        memberId: member.internalId,
        locationId: location.internalId,
        uploadId: upload.id
      });

      const content = await DSAApi.setScreenContent(active?.serial!!, {
        type: "BOARD",
        sourceId: board.data.id,
        playable: true
      });

      setLoadingContent(false);
      setScreens([{ ...content.data, visible: true }, ...screens.filter(it => it.serial !== content.data.serial)]);
    } catch (e) {
      setLoadingContent(false);
      handleServerError(e);
    }
  }


  function onSearchChange(search: string) {
    setSearch(search);
    screens.forEach(sc => {
      const scName = sc.name!.toLowerCase();
      const scLoc = locations.find(loc => loc.internalId === sc.locationId)!.name!.toLowerCase();
      const c = sc.content!;
      let scContentName = '';
      if (c.type === 'BOARD') {
        scContentName = boards.find(it => it.id === c.sourceId)?.name?.toLowerCase() || '';
      } else if (c.type === 'PLAYLIST') {
        scContentName = playlists.find(it => it.id === c.sourceId)?.name?.toLowerCase() || '';
      } else if (c.type === 'SCHEDULE') {
        scContentName = schedules.find(it => it.id === c.sourceId)?.name?.toLowerCase() || '';
      }
      sc.visible = scName.includes(search.toLowerCase()) || scLoc!.includes(search.toLowerCase()) || scContentName.includes(search.toLowerCase());
    })
    setScreens([...screens]);
  }

  function lockedForUser(screen: DsaScreenDto) {
    return !member.owner && screen.locked;
  }

  function openManageScreenModal(screen: DsaScreenDto) {
    setActive(screen);
    setManageScreenModal(true);
  }

  function openPreviewModal(screen: DsaScreenDto) {
    setPreviewModal(true);
    const content = screen.content!;
    if (content.type === 'BOARD') {
      setPreviewEntries([]);
      const previewBoard = boards.find(b => b.id === content.sourceId)!;
      if (previewBoard.type === 'UPLOAD') {
        setPreviewType('upload');
        setPreviewUploadId(previewBoard.uploadId!);
        setPreviewBoard({});
      } else {
        setPreviewType('board');
        setPreviewBoard(previewBoard);
        setPreviewUploadId('');
      }
    } else if (content.type === 'PLAYLIST') {
      setPreviewType('playlist');
      setPreviewEntries(playlists.find(b => b.id === content.sourceId)!.entries!.sort((a, b) => { return a.sequence! - b.sequence! }));
      setPreviewBoard({});
      setPreviewUploadId('');
    }
  }

  function rebootScreen(screen: DsaScreenDto) {
    if (lockedForUser(screen)) return;
    setActive(screen);
    setRebootScreenModal(true);
  }

  function showDiagnostics(screen: DsaScreenDto) {
    if (lockedForUser(screen)) return;
    setActive(screen);
    setDiagnosticsScreenModal(true);
  }

  function removeScreen(screen: DsaScreenDto) {
    if (lockedForUser(screen)) return;

    setActive(screen);
    setRemoveScreenModal(true);
  }

  function onCloseRebootScreenModal(fetch: boolean) {
    setRebootScreenModal(false);
    if (fetch) {
      setScreensFetched(false);
      fetchDevices();
    }
  }

  function onCloseDiagnosticsScreenModal(fetch: boolean) {
    setDiagnosticsScreenModal(false);
    if (fetch) {
      setScreensFetched(false);
      fetchDevices();
    }
  }

  function onCloseRemoveScreenModal(fetch: boolean) {
    setRemoveScreenModal(false);
    if (fetch) {
      setScreensFetched(false);
      fetchDevices();
      fetchAvailableDevices();
      fetchDeactivatedDevices();
    }
  }

  function deactivateScreen(it: number) {
    setActiveNumber(it);
    setDeactivateScreenModal(true);
  }

  function onCloseDeactivateScreenModal(fetch: boolean) {
    setDeactivateScreenModal(false);
    if (fetch) {
      fetchAvailableDevices();
      fetchDeactivatedDevices();
    }
  }

  function reactivateScreenLicense(callback?: () => void) {
    setReactivating(true);
    window.MHM.ProModal.reactivateDSALicense({ memberId: member.id!, locationId: activeLocation.internalId }).then(() => {
      fetchAvailableDevices();
      fetchDeactivatedDevices();
      setReactivating(false);
      callback?.();
    });
  }

  function withTooltip(tooltip: string, object: ReactNode) {
    return <Tooltip placement="bottom" title={tooltip} getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}>
      {object}
    </Tooltip>
  }

  function triggerOpenMenu(it: DsaScreenDto) {
    document.getElementById(it.serial!)?.click();
  }

  function getLocNameFromId(locId: number) {
    return locations.find(loc => loc.internalId === locId)!.name!;
  }

  function onLocSortClick() {
    if (sortType === SortType.locNameAscending) {
      setScreens([...screens.reverse()]);
      setSortType(SortType.locNameDescending);
    } else {
      screens.sort((a, b) => getLocNameFromId(a.locationId!).toLowerCase().localeCompare(getLocNameFromId(b.locationId!).toLowerCase()));
      setScreens([...screens]);
      setSortType(SortType.locNameAscending);
    }
  }

  function onScreenNameSortClick() {
    if (sortType === SortType.screenNameAscending) {
      setScreens([...screens.reverse()]);
      setSortType(SortType.screenNameDescending);
    } else {
      screens.sort((a, b) => a.name!.toLowerCase().localeCompare(b.name!.toLowerCase()));
      setScreens([...screens]);
      setSortType(SortType.screenNameAscending);
    }
  }

  function onContentTypeSortClick() {
    if (sortType === SortType.contentTypeAscending) {
      setScreens([...screens.reverse()]);
      setSortType(SortType.contentTypeDescending);
    } else {
      screens.sort((a, b) => a.content!.type!.localeCompare(b.content!.type!));
      setScreens([...screens]);
      setSortType(SortType.contentTypeAscending);
    }
  }

  function onSortMobile(sort: SortType) {
    if (sort === SortType.screenNameAscending || sort === SortType.screenNameDescending) {
      screens.sort((a, b) => a.name!.toLowerCase().localeCompare(b.name!.toLowerCase()));
      if (sort === SortType.screenNameDescending) {
        screens.reverse();
      }
    } else if (sort === SortType.locNameAscending || sort === SortType.locNameDescending) {
      screens.sort((a, b) => getLocNameFromId(a.locationId!).toLowerCase().localeCompare(getLocNameFromId(b.locationId!).toLowerCase()));
      if (sort === SortType.locNameDescending) {
        screens.reverse();
      }
    }
    setScreens([...screens]);
    setSortType(sort);
    setMobileSortMenuVisible(false);
  }

  function filterAllLocs() {
    setFilterAllLocs(true);
    setFilteredLocs([]);
    filterScreens(true, undefined, undefined, undefined, undefined, undefined);
  }

  function filterAllStatuses() {
    setFilterAllStatuses(true);
    setFilteredStatuses([]);
    filterScreens(undefined, undefined, true, undefined, undefined, undefined);
  }

  function filterAllContentTypes() {
    setFilterAllContentTypes(true);
    setFilteredContentTypes([]);
    filterScreens(undefined, undefined, undefined, undefined, true, undefined);
  }

  function toggleFilterLoc(locId: number, checked: boolean) {
    let newFilteredLocs = filteredLocs;
    if (checked) {
      newFilteredLocs = [...newFilteredLocs, locId];
      setFilterAllLocs(false);
    } else {
      newFilteredLocs.splice(filteredLocs.indexOf(locId), 1);
    }
    setFilteredLocs([...newFilteredLocs]);
    if (newFilteredLocs.length === 0) {
      setFilterAllLocs(true);
      filterScreens(true, [], undefined, undefined, undefined, undefined);
    } else {
      filterScreens(false, newFilteredLocs, undefined, undefined, undefined, undefined);
    }
  }

  function toggleFilterStatus(status: string, checked: boolean) {
    let newFilteredStatuses = filteredStatuses;
    if (checked) {
      newFilteredStatuses = [...newFilteredStatuses, status];
      setFilterAllStatuses(false);
    } else {
      newFilteredStatuses.splice(filteredStatuses.indexOf(status), 1);
    }
    setFilteredStatuses([...newFilteredStatuses]);
    if (newFilteredStatuses.length === 0) {
      setFilterAllStatuses(true);
      filterScreens(undefined, undefined, true, [], undefined, undefined);
    } else {
      filterScreens(undefined, undefined, false, newFilteredStatuses, undefined, undefined);
    }
  }

  function toggleFilterContentType(contentType: string, checked: boolean) {
    let newFilteredContentTypes = filteredContentTypes;
    if (checked) {
      newFilteredContentTypes = [...newFilteredContentTypes, contentType];
      setFilterAllContentTypes(false);
    } else {
      newFilteredContentTypes.splice(filteredContentTypes.indexOf(contentType), 1);
    }
    setFilteredContentTypes([...newFilteredContentTypes]);
    if (newFilteredContentTypes.length === 0) {
      setFilterAllContentTypes(true);
      filterScreens(undefined, undefined, undefined, undefined, true, []);
    } else {
      filterScreens(undefined, undefined, undefined, undefined, false, newFilteredContentTypes);
    }
  }

  function filterScreens(
    filterAllLocsParam: boolean = isFilterAllLocs, filteredLocsParam: number[] = filteredLocs,
    filterAllStatusesParam: boolean = isFilterAllStatuses, filteredStatusesParam: string[] = filteredStatuses,
    filterAllContentTypesParam: boolean = isFilterAllContentTypes, filteredContentTypesParam: string[] = filteredContentTypes
  ) {
    screens.forEach(sc => {
      sc.visible = false;
    });

    screens.forEach(sc => {
      const hasLocation = filterAllLocsParam || filteredLocsParam.includes(sc.locationId!);
      const isOnline = sc.online ? 'online' : 'offline';
      const hasOnlineStatus = filterAllStatusesParam || filteredStatusesParam.includes(isOnline);
      const hasContentType = filterAllContentTypesParam || (sc.content ? filteredContentTypesParam.includes(sc.content.type!) : filteredContentTypesParam.includes(DsaContentDtoFilterTypeEnum.None));
      if (hasLocation && hasOnlineStatus && hasContentType) {
        sc.visible = true;
      }
    });
    setScreens([...screens]);
    setNumFilters(filteredLocsParam.length + filteredStatusesParam.length + filteredContentTypesParam.length);
  }

  function onFilterClearClick() {
    filterAllLocs();
    filterAllStatuses();
    filterAllContentTypes();
    setFilteredLocs([]);
    setFilteredStatuses([]);
    setFilteredContentTypes([]);
    screens.forEach(sc => {
      sc.visible = true;
    })
    setScreens([...screens]);
    setMobileFilterMenuVisible(false);
    setNumFilters(0);
  }

  function onMobileFilterClick() {
    setMobileFilterMenuVisible(false);
  }

  function onScreenAvailabilityFilterChange(availType: AvailabilityType) {
    filterAllStatuses();
    filterAllContentTypes();
    setFilteredStatuses([]);
    setFilteredContentTypes([]);
    filterScreens(undefined, undefined, true, [], true, []);
    setScreenAvailabilityFilter(availType);
  }

  const doubleDash = <>&mdash;&nbsp;&mdash;</>;

  const filterMenuList = <ul>
    <div className="label">Locations</div>
    <li key={'allLocs'}>
      <Radio checked={isFilterAllLocs && filteredLocs.length === 0}
        onClick={() => filterAllLocs()} />
      <span>All Locations</span>
    </li>
    {locations.length <= 8 && locations.map(loc => {
      return <li key={loc.id}>
        <Checkbox checked={filteredLocs.includes(loc.internalId!)}
          onChange={(ev) => toggleFilterLoc(loc.internalId!, ev.target.checked)} />
        <span>{loc.name!}</span>
      </li>
    })}
    {locations.length > 8 &&
      <>
        {Array.from(Array(8).keys()).map(i => {
          const loc = locations[i];
          return <li key={loc.id}>
            <Checkbox checked={filteredLocs.includes(loc.internalId!)}
              onChange={(ev) => toggleFilterLoc(loc.internalId!, ev.target.checked)} />
            <span>{loc.name!}</span>
          </li>
        })}
        {!showAllFilterLocations &&
          <div className="show-more-locs" onClick={() => setShowAllFilterLocations(true)}>Show more</div>}
        {showAllFilterLocations && locations.slice(8).map(loc => {
          return <li key={loc.id}>
            <Checkbox checked={filteredLocs.includes(loc.internalId!)}
              onChange={(ev) => toggleFilterLoc(loc.internalId!, ev.target.checked)} />
            <span>{loc.name!}</span>
          </li>
        })}
      </>}

    {(screenAvailabilityFilter === AvailabilityType.active || screenAvailabilityFilter === AvailabilityType.all) &&
      <>
        <div className="separator" />

        <div className="label">Status</div>
        <li>
          <Radio checked={isFilterAllStatuses && filteredStatuses.length === 0}
            onClick={() => filterAllStatuses()} />
          <span>All Statuses</span>
        </li>
        <li><Checkbox checked={filteredStatuses.includes('online')} onChange={(ev) => toggleFilterStatus('online', ev.target.checked)} />Online
        </li>
        <li><Checkbox checked={filteredStatuses.includes('offline')} onChange={(ev) => toggleFilterStatus('offline', ev.target.checked)} />Offline
        </li>
        {screenAvailabilityFilter !== AvailabilityType.active && <li>
          <Checkbox checked={filteredStatuses.includes('deactivated')} onChange={(ev) => toggleFilterStatus('deactivated', ev.target.checked)} />Deactivated
        </li>}

        <div className="separator" />

        <div className="label">Assigned Content Type</div>
        <li>
          <Radio checked={isFilterAllContentTypes && filteredContentTypes.length === 0}
            onClick={() => filterAllContentTypes()} />
          <span>All Content Types</span>
        </li>
        <li><Checkbox checked={filteredContentTypes.includes(DsaContentDtoFilterTypeEnum.Board)}
          onChange={(ev) => toggleFilterContentType(DsaContentDtoFilterTypeEnum.Board, ev.target.checked)} />Design
        </li>
        <li><Checkbox checked={filteredContentTypes.includes(DsaContentDtoFilterTypeEnum.Playlist)}
          onChange={(ev) => toggleFilterContentType(DsaContentDtoFilterTypeEnum.Playlist, ev.target.checked)} />Playlist
        </li>
        <li><Checkbox checked={filteredContentTypes.includes(DsaContentDtoFilterTypeEnum.Schedule)}
          onChange={(ev) => toggleFilterContentType(DsaContentDtoFilterTypeEnum.Schedule, ev.target.checked)} />Schedule
        </li>
        <li><Checkbox checked={filteredContentTypes.includes(DsaContentDtoFilterTypeEnum.None)}
          onChange={(ev) => toggleFilterContentType(DsaContentDtoFilterTypeEnum.None, ev.target.checked)} />None</li>
      </>}
  </ul>;

  const filterFooter = <div className="filter-footer">
    <Button className={`clear-btn`} onClick={() => onFilterClearClick()}>Clear</Button>
    <Button className={!filteredLocs.length && !filteredStatuses.length && !filteredContentTypes.length ? 'disabled' : ''}
      type="primary" onClick={() => onMobileFilterClick()}>Filter</Button>
  </div>

  function onSetupModalClose() {
    setSetUpModal(false);
  }

  return <div>
    {loading && <div className={'dsa'}>
      <div className={'spinner'}><Spinner /></div>
    </div>}

    {!loading && <section className={`${mobile ? "dsa" : "dsa full-content"} ${(hasScreens || member.enterpriseDisplayAccount) ? 'with-screens' : ''}`}>
      {showData &&
        <header>
          {mobile && screens.length > 0 &&
            <Input type="search" prefix={<SearchOutlined />} placeholder="Search by location, screen or content names" value={search}
              onChange={ev => onSearchChange(ev.target.value)} />}
          {mobile && (screens.length > 0 || !!availableDevices) &&
            <Button className="order-screen" type="primary" onClick={() => onOrderDevice()}>Order</Button>}

          <div className="availability-filters">
            <ul>
              <li className={screenAvailabilityFilter === AvailabilityType.active ? 'active' : ''} onClick={() => onScreenAvailabilityFilterChange(AvailabilityType.active)}>Active ({screens.length})</li>
              <li className={screenAvailabilityFilter === AvailabilityType.new ? 'active' : ''} onClick={() => onScreenAvailabilityFilterChange(AvailabilityType.new)}>New ({availableDevices})</li>
              <li className={screenAvailabilityFilter === AvailabilityType.deactivated ? 'active' : ''} onClick={() => onScreenAvailabilityFilterChange(AvailabilityType.deactivated)}>Deactivated ({deactivatedDevices})</li>
              <li className={screenAvailabilityFilter === AvailabilityType.all ? 'active' : ''} onClick={() => onScreenAvailabilityFilterChange(AvailabilityType.all)}>All ({screens.length + availableDevices + deactivatedDevices})</li>
            </ul>
          </div>
          {!mobile && screens.length > 0 &&
            <Input type="search" prefix={<SearchOutlined />} placeholder="Search by location, screen or content name" value={search}
              onChange={ev => onSearchChange(ev.target.value)} />}
        </header>}

      {showData && <div className="filter-sidebar" style={bannerOffset ? { top: 157 + bannerOffset, height: `calc(100vh - ${157 + bannerOffset}px)` } : undefined}>
        {filterMenuList}
      </div>}

      {showData && hasScreens && <div className={"tb-head"}>
        <span className="status-col"></span>
        <span className="location-col" onClick={() => onLocSortClick()}>
          Location
          <SortScreenIcon ascending={sortType === SortType.locNameAscending} descending={sortType === SortType.locNameDescending} />
        </span>
        <span className="screen-col" onClick={() => onScreenNameSortClick()}>
          {!mobile && <>
            Screen
            <SortScreenIcon ascending={sortType === SortType.screenNameAscending} descending={sortType === SortType.screenNameDescending} />
          </>}
        </span>
        <span className="content-col" onClick={() => onContentTypeSortClick()}>
          Assigned Content
          <SortScreenIcon ascending={sortType === SortType.contentTypeAscending} descending={sortType === SortType.contentTypeDescending} />
        </span>
        <span className="hdmi-col">HDMI</span>
        <span className="wifi-col">Wi-Fi</span>
        {!member.owner && <span className="locked-col">Locked</span>}
        <span className="menu-col">
          <div style={{ width: "30px", display: "flex" }}>
            {mobile && <>
              <div className="sort-btn" onClick={() => setMobileSortMenuVisible(!isMobileSortMenuVisible)}>
                <img src="https://timber.mhmcdn.com/site/marketing/icons/Sort.svg" alt="sort" />
              </div>
              <Drawer
                placement={"bottom"}
                open={isMobileSortMenuVisible}
                onClose={() => setMobileSortMenuVisible(false)}
                closeIcon={<DownOutlined />}
                className={"mobile-bottom-menu sort-menu"}
                height={350}>
                <div className="mobile-menu-header">
                  Sort by
                </div>
                <ul>
                  <li onClick={() => onSortMobile(SortType.locNameAscending)}>
                    <ArrowUpOutlined />
                    Location (Alphabetical A-Z)
                  </li>
                  <li onClick={() => onSortMobile(SortType.locNameDescending)}>
                    <ArrowDownOutlined />
                    Location (Alphabetical Z-A)
                  </li>
                  <div className="separator" />
                  <li onClick={() => onSortMobile(SortType.screenNameAscending)}>
                    <ArrowUpOutlined />
                    Screen Name (Alphabetical A-Z)
                  </li>
                  <li onClick={() => onSortMobile(SortType.screenNameDescending)}>
                    <ArrowDownOutlined />
                    Screen Name (Alphabetical Z-A)
                  </li>
                </ul>
              </Drawer>
            </>}

            {mobile && <>
              <div className="filter-btn" onClick={() => setMobileFilterMenuVisible(!isMobileFilterMenuVisible)}>
                <FilterOutlined />
                {numFilters > 0 && <div className="num-filters">{numFilters}</div>}
              </div>
              <Drawer
                placement={"bottom"}
                open={isMobileFilterMenuVisible}
                onClose={() => setMobileFilterMenuVisible(false)}
                closeIcon={<DownOutlined />}
                className={"mobile-bottom-menu filter-menu"}
                height={500}>
                <div className="mobile-menu-header">
                  Filter
                </div>
                {filterMenuList}
                {filterFooter}
              </Drawer>
            </>}
          </div>
        </span>
      </div>}

      <div className={`${!showData ? "white-bg" : "screens"} ${!hasScreens && member.enterpriseDisplayAccount ? 'full-height' : ''}`}>
        {!showData &&
          <div className={"no-data promo"}>
            <div className="scroll">
              <div className="text">
                <h2>Update your screens with ease</h2>
                <h4 className="sub-title">Save countless hours and reduce hassle by sending your menu edits right to your screens.<br />MustHaveMenus Display App greatly simplifies your menu screen workflows across every location you have!</h4>
                <div className="video-container">
                  <video src="https://timber.mhmcdn.com/site/marketing/homepage/dashboard/display-splash-av56m.mp4" autoPlay loop muted playsInline />
                </div>
                {isProSubscription(member) && <Button type={"primary"} size={"large"} onClick={() => onOrderDevice()}>Order Now</Button>}
                {!isProSubscription(member) && <Button type={"primary"} size={"large"} onClick={() => onUpgradeClick()}>Upgrade to Pro</Button>}
                <div className="learn-more">
                  <a href="/feature/book-demo-auth.html" target="_blank" rel="noreferrer">Book a Demo</a>
                  &nbsp;&nbsp;|&nbsp;&nbsp;
                  <a href="/feature/digital-menu-board-solution.html" target="_blank" rel="noreferrer">Learn More</a>
                </div>
              </div>
              <div className="testimonial">
                <div className="opening">
                  <img src={OpeningQuotes} alt="quotes" />
                  <img src={BorderTop} alt="border" />
                </div>
                <div className="message">
                  <div className="message-content">
                    <span>“We have been using their services for printed menus and love the new digital display products. Intuitive, classy and easy way to keep your menus up to date all the time. It has been great for our small pub!”</span>
                    <div className="company">
                      <h4>Cory</h4>
                      <h5>Cooper Plank</h5>
                    </div>
                  </div>
                  <img src={CooperPlank} alt="cooper-plank" />
                </div>
                <div className="closing">
                  <img src={BorderBottom} alt="border" />
                  <img src={ClosingQuotes} alt="quotes" />
                </div>
              </div>
            </div>
          </div>
        }

        {showData &&
          <ul>
            {screens.length > 0 && (screenAvailabilityFilter === AvailabilityType.all || screenAvailabilityFilter === AvailabilityType.active) && (() => {
              const visibleScreens = screens.filter(it => it.visible);
              return <>
                {visibleScreens
                  .slice(0, visibleCount)
                  .map(it => <li className={"screen"} key={it.serial}>
                    {(!loadingContent || active?.serial !== it.serial) && <ScreenCard
                      it={it}
                      screens={screens}
                      setScreens={setScreens}
                      boards={boards}
                      playlists={playlists}
                      schedules={schedules}
                      uploads={uploads}
                      lockedForUser={lockedForUser(it)}
                      setActive={setActive}
                      setEditContentModal={setEditContentModal}
                      openPreviewModal={openPreviewModal}
                      openManageScreenModal={openManageScreenModal}
                      rebootScreen={rebootScreen}
                      showDiagnostics={showDiagnostics}
                      removeScreen={removeScreen}
                      withTooltip={withTooltip}
                    />}
                    {(loadingContent && active?.serial === it.serial) && <Spinner />}
                  </li>)}
                {visibleScreens.length > visibleCount &&
                  <li className={"show-more-screens"}>
                    <span onClick={() => setVisibleCount(c => c + SCREENS_BATCH_SIZE)}>Show more</span>
                  </li>}
              </>;
            })()}
            {!!availableDevices && !search && (isFilterAllStatuses || filteredStatuses.includes('offline')) && (screenAvailabilityFilter === AvailabilityType.all || screenAvailabilityFilter === AvailabilityType.new) && Object.keys(availableDevicesPerLocation).map((locId, idx) => {
              return Array.from(
                Array(availableDevicesPerLocation[parseInt(locId)]).keys()
              ).map((it) => {
                return (
                  <AvailableScreenCard
                    key={locId + it}
                    it={idx + it}
                    locationName={locations.find((l) => l.internalId === parseInt(locId))?.name || ""}
                    deactivateScreen={deactivateScreen}
                    setSetupModal={setSetUpModal}
                    withTooltip={withTooltip}
                  />
                );
              });
            })}
            {!!deactivatedDevices && !search && (isFilterAllStatuses || filteredStatuses.includes('deactivated')) && (screenAvailabilityFilter === AvailabilityType.all || screenAvailabilityFilter === AvailabilityType.deactivated) && Array.from(Array(deactivatedDevices).keys()).map(it => {
              return <li className="screen" key={it}>
                <span className="status-col">
                  <span className={"status-indicator deactivated"}>deactivated</span>
                </span>
                <span className="location-col">{doubleDash}</span>
                <span className="screen-col">
                  {!mobile && <div className="playpause-btn offline">
                    <div className="icon"><StartPlayingIcon /></div>
                  </div>}
                  <div className="thumbnail-container no-content">Deactivated {it + 1}</div>
                  &nbsp;&nbsp;&nbsp;<i>Screen Name</i>
                  {mobile && <Button className={"reactivate"} disabled={reactivating} onClick={() => reactivateScreenLicense()}>Reactivate License</Button>}
                </span>
                <span className="content-col"><Button className={"reactivate"} disabled={reactivating} onClick={() => reactivateScreenLicense()}>Reactivate License</Button></span>
                <span className="hdmi-col"><CloseCircle /></span>
                <span className="wifi-col">
                  <div>
                    <span><WiFiSignalOff /></span>
                    <span>WiFi Unavailable</span>
                  </div>
                </span>
                {!member.owner && <span className="locked-col">
                </span>}
                {!mobile && <span className="menu-col"><div style={{ width: "30px" }}></div></span>}
              </li>
            })}
            {search && screens.length > 0 && screens.filter(it => it.visible).length === 0 && <div className="no-matches">
              <span>No matches found.</span>
            </div>}
          </ul>}

        {!hasScreens && member.enterpriseDisplayAccount && <div className={'pair-device-wrapper'}>
          <DisplayIconSecondary />
          <Button className="pair-screen" type="primary" onClick={onPairNewDevice}>Pair New Device</Button>
        </div>}
      </div>

      <ScreenSetUpModal
        isVisible={isSetUpModal}
        onClose={onSetupModalClose}
        onSaved={onScreenAdded}
        seeHelp={() => setSetUpHelpModal(true)}
        withTooltip={withTooltip}
      />
      <ScreenSetUpHelpModal
        isVisible={isSetUpHelpModal}
        onClose={() => setSetUpHelpModal(false)}
      />

      <ManageScreenModal
        isVisible={isManageScreenModal}
        onClose={onCloseManageScreenModal}
        onSaved={onScreenUpdated}
        currentScreen={active!}
        withTooltip={withTooltip}
        openMobileMenu={() => triggerOpenMenu(active!)}
      />

      <EditScreenContentModal
        id={active?.serial}
        screen={active}
        locationId={location.internalId}
        isVisible={isEditContentModal}
        withTooltip={withTooltip}
        onDesignSelect={onDesignSelect}
        onPlaylistSelect={onPlaylistSelect}
        onScheduleSelect={onScheduleSelect}
        onUploadSelect={onUploadSelect}
        onClose={() => setEditContentModal(false)}
        openMobileMenu={() => triggerOpenMenu(active!)}
      />

      <PreviewModal
        previewType={previewType}
        previewBoard={previewBoard}
        previewEntries={previewEntries}
        previewUploadId={previewUploadId}
        isVisible={isPreviewModal}
        onClose={() => setPreviewModal(false)}
        openMobileMenu={() => triggerOpenMenu(active!)}
      />

      <RebootScreenModal
        isVisible={isRebootScreenModal}
        onClose={onCloseRebootScreenModal}
        active={active!}
      />

      <DisplayDiagnosticsOnScreenConfirmationModal
        isVisible={isDiagnosticsScreenModal}
        onClose={onCloseDiagnosticsScreenModal}
        active={active!}
      />

      <RemoveScreenModal
        isVisible={isRemoveScreenModal}
        onClose={onCloseRemoveScreenModal}
        active={active!}
      />

      <DeactivateScreenModal
        it={activeNumber}
        isVisible={isDeactivateScreenModal}
        onClose={onCloseDeactivateScreenModal}
      />
    </section>}
  </div>
}
